import React, { useState, useEffect } from 'react';
import { Button, Modal, Text, Select } from 'v3/components';
import { Row, Col } from 'react-bootstrap';
import { useClient } from '../../context';
import { useSnackbar } from 'v3/components/Snackbar';
import { cooperplaceApi } from 'services/api';

import {
  Label,
  ColorCircle,
  ColorPreviewDot,
  ColorPreviewText,
} from '../styles';

const AVAILABLE_COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // orange
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

export default function NewTypeModal({ isOpen, onClose, onSave }) {

  const {
    data,
    occurrenceTypesClient,
    setOccurrenceTypesClient
  } = useClient();

  const snackbar = useSnackbar();

  const [occurrenceColors, setOccurrenceColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedOccurrences, setSelectedOccurrences] = useState([]);

  const currentTypeIds = new Set((data?.occurrenceClientTypes || []).map((type) => type?.id));
  const availableOccurrenceTypes = (occurrenceTypesClient || []).filter(
    (occurrence) => !currentTypeIds.has(occurrence?.id)
  );

  useEffect(() => {
    async function fetchOccurrenceTypes() {
      try {
        setLoading(true);
        const response = await cooperplaceApi.get('occurrences/client/pre-config');
        setOccurrenceTypesClient(response.data || []);
      } catch (ex) {
        snackbar.show(<Text>Não foi possível carregar os tipos de ocorrências</Text>, {
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }

    if (isOpen) {
      fetchOccurrenceTypes();
    }

  }, [isOpen]);

  function handleClose() {
    setOccurrenceColors({});
    setSelectedOccurrences([]);
    onClose();
  }

  function handleOccurrencesChange(value) {
    const nextSelected = value || [];

    setSelectedOccurrences(nextSelected);
    setOccurrenceColors((prev) => {
      const next = {};

      nextSelected.forEach((occurrence, index) => {
        next[occurrence.id] = prev[occurrence.id] || AVAILABLE_COLORS[index % AVAILABLE_COLORS.length];
      });

      return next;
    });
  }

  function handleColorChange(occurrenceId, color) {
    setOccurrenceColors((prev) => ({
      ...prev,
      [occurrenceId]: color,
    }));
  }

  function handleAdd() {
    if (!selectedOccurrences.length) {
      snackbar.show(<Text>Selecione pelo menos um tipo de ocorrência</Text>, {
        type: 'error',
      });
      return;
    }

    const uniqueSelected = selectedOccurrences.filter(
      (occurrence) => !currentTypeIds.has(occurrence?.id)
    );

    if (!uniqueSelected.length) {
      snackbar.show(<Text>Esse tipo de ocorrência já foi adicionado</Text>, {
        type: 'warning',
      });
      return;
    }

    if (uniqueSelected.length < selectedOccurrences.length) {
      snackbar.show(<Text>Alguns tipos já existiam e não foram adicionados</Text>, {
        type: 'warning',
      });
    }

    const payload = uniqueSelected.map((occurrence, index) => ({
      ...occurrence,
      color: occurrenceColors[occurrence.id] || AVAILABLE_COLORS[index % AVAILABLE_COLORS.length],
    }));

    onSave(payload);

    handleClose();
  }

  return (
    <Modal
      size="lg"
      show={isOpen}
      heading={
        <Text type="header">
          {'Novo Tipo de Ocorrência'}
        </Text>
      }
      handleClose={handleClose}
      body={
        <Row>
          <Col md={12} xs={12} className="mb-2">
            <Select
              loading={loading}
              label="Tipo de ocorrência"
              options={availableOccurrenceTypes}
              multiple={true}
              value={selectedOccurrences}
              onChange={handleOccurrencesChange}
              getOptionValue={option => option.id}
              getOptionLabel={option => option.name}
            />
          </Col>
          {!!selectedOccurrences.length && (
            <Col md={12} xs={12} className="mb-2">
              <Label>Cor das ocorrências selecionadas</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selectedOccurrences.map((occurrence) => (
                  <div
                    key={occurrence.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ColorPreviewDot color={occurrenceColors[occurrence.id]} size="small" />
                      <ColorPreviewText>{occurrence.name}</ColorPreviewText>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {AVAILABLE_COLORS.map((color) => (
                        <ColorCircle
                          key={`${occurrence.id}-${color}`}
                          type="button"
                          color={color}
                          size="small"
                          selected={occurrenceColors[occurrence.id] === color}
                          onClick={() => handleColorChange(occurrence.id, color)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          )}
        </Row>
      }
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={handleClose} loading={loading}>
            Cancelar
          </Button>
          <Button onClick={handleAdd} loading={loading}>
            Adicionar
          </Button>
        </div>
      }
    />
  );
}
